import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Alert from "components/ui/alert/Alert";
import Badge from "components/ui/badge/Badge";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import { authBaseUrl } from "lib/auth/authClient";
import type { SsoProviderResponse } from "lib/hooks/api/auth/sso/useSsoProviders";
import { downloadFileViaFetch } from "lib/utils";
import { DeleteSsoProviderModal } from "./DeleteSsoProviderModal";
import DomainVerificationSection from "./DomainVerificationSection";

type SsoProvider = SsoProviderResponse;

type SsoProviderCardProps = {
  provider: SsoProvider;
};

export type { SsoProvider };

export default function SsoProviderCard({ provider }: SsoProviderCardProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const isSaml = !!provider.samlConfig;
  const domains = provider.domain.split(",").map((d) => d.trim());

  return (
    <>
      <Card borderRadius={8} padding={16}>
        <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={8}>
          <Flex column gap={8} width="100%">
            <Flex gap={8} alignItems="center">
              <Text h3>{isSaml ? "SAML" : "OIDC"}</Text>
              <Badge fitContent badgeType={provider.domainVerified ? "success" : "warning"}>
                {provider.domainVerified ? "Verified" : "Unverified"}
              </Badge>
            </Flex>

            {isSaml ? <SamlDetails provider={provider} /> : <OidcDetails provider={provider} />}
            <Flex gap={8} alignItems="center" width="100%">
              <DetailLabel>Domains</DetailLabel>
              <Text>{domains.join(", ")}</Text>
            </Flex>
          </Flex>
          <Flex gap={8}>
            <Button
              as="a"
              to="/account-management/sso/$providerId"
              params={{ providerId: provider.providerId }}
              buttonType="secondary"
            >
              Edit
            </Button>
            <Button
              buttonType="secondary"
              buttonStyle="destructive"
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </Flex>
        </Flex>

        {!provider.domainVerified && (
          <VerificationArea>
            <Flex column gap={16}>
              <Alert type="warning">
                Users cannot sign in with this provider yet. Publish the DNS TXT record below, then
                verify the domain to finish setup.
              </Alert>
              <DomainVerificationSection
                providerId={provider.providerId}
                domains={domains}
                domainVerified={provider.domainVerified}
                onVerified={() => void 0}
              />
            </Flex>
          </VerificationArea>
        )}
      </Card>

      <DeleteSsoProviderModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        providerId={provider.providerId}
      />
    </>
  );
}

function OidcDetails({ provider }: { provider: SsoProvider }) {
  const callbackUrl = `${authBaseUrl}/sso/callback/${provider.providerId}`;

  return (
    <>
      <Flex gap={8} alignItems="center" width="100%">
        <DetailLabel>Issuer</DetailLabel>
        <Text as="code">{provider.issuer}</Text>
      </Flex>
      <Flex gap={8} alignItems="center" width="100%">
        <DetailLabel>Callback URL</DetailLabel>
        <Text as="code">{callbackUrl}</Text>
      </Flex>
    </>
  );
}

function SamlDetails({ provider }: { provider: SsoProvider }) {
  const acsUrl = `${authBaseUrl}/sso/saml2/sp/acs/${provider.providerId}`;
  const metadataUrl = `${authBaseUrl}/sso/saml2/sp/metadata?providerId=${provider.providerId}`;

  return (
    <>
      <Flex gap={8} alignItems="center" width="100%">
        <DetailLabel>SSO URL</DetailLabel>
        <Text as="code">{provider.samlConfig?.entryPoint}</Text>
      </Flex>
      <Flex gap={8} alignItems="center" width="100%">
        <DetailLabel>ACS URL</DetailLabel>
        <Text as="code">{acsUrl}</Text>
      </Flex>
      <Flex gap={8} alignItems="center" width="100%">
        <DetailLabel>Entity ID</DetailLabel>
        <Text as="code">{provider.issuer}</Text>
      </Flex>
      <Flex gap={8} alignItems="center" width="100%">
        <DetailLabel>SP Metadata</DetailLabel>
        <Flex gap={8} alignItems="center" justifyContent="space-between" width="100%">
          <a href={metadataUrl} target="_blank" rel="noopener noreferrer">
            {metadataUrl}
          </a>
          <Button
            buttonStyle="inverse"
            icon="download01"
            iconProps={{ size: 16 }}
            onClick={() =>
              downloadFileViaFetch(metadataUrl, `stotles-sp-metadata-${provider.providerId}.xml`)
            }
          />
        </Flex>
      </Flex>
    </>
  );
}

const DetailLabel = styled(Text)(({ theme }) => ({
  width: 100,
  flexShrink: 0,
  fontSize: 13,
  fontWeight: theme.font.fontWeightMedium,
  color: theme.colors.sysTextSecondary,
}));

const VerificationArea = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  marginTop: 8,
  paddingTop: 16,
}));
